import React, { useEffect, useRef } from 'react';

import config from 'config';
import PropTypes from 'prop-types';
import { FaChevronDown } from 'react-icons/fa';
import { defineMessages } from 'react-intl';
import styled, { css, ThemeProvider } from 'styled-components';

import { useConfigValue } from '~/shared/utils/config';
import {
  Collapsible,
  mobile,
  desktop,
  useCollapsibleState,
  useProductDetailContext,
} from '~/techstyle-shared/react-components';
import { FormattedMessage, useIntl } from '~/techstyle-shared/react-intl';

const dynamicDefineMessages = defineMessages;

const DetailContent = styled.div`
  font-size: 14px;

  &:not(:last-child) {
    margin-bottom: 10px;
  }
`;

const DetailTitle = styled.div`
  font-weight: 700;
  margin-bottom: 10px;
`;

const DetailSubTitle = styled(DetailTitle)`
  font-size: 14px;
  margin-bottom: 6px;
`;

const CollapsibleButton = styled(Collapsible.Button)(({ theme, isOpen }) => {
  const { borderLight, sublabelBlack } = theme.colors;
  const altButtonBorder = css`
    border-left: 0;
    border-radius: 0;
    border-right: 0;
  `;

  const altButtonStyles = theme.showBundleStyles
    ? css`
        ${altButtonBorder}
        border-bottom: 0;
        border-top-color: ${borderLight};
        width: 100%;
        box-shadow: inset 0 1px 0 0 ${borderLight};
        ${desktop`
          box-shadow: none;
          border-top: 1px solid ${borderLight};
        `}
      `
    : mobile`
      ${altButtonBorder}
    `;

  return css`
    align-items: center;
    border-radius: 4px;
    box-shadow:
      inset 0 -1px 0 0 ${borderLight},
      inset 0 1px 0 0 ${borderLight};
    color: ${sublabelBlack};
    display: flex;
    font-size: 14px;
    font-weight: 700;
    justify-content: space-between;
    padding: 16px;
    &:focus-visible {
      outline: none;
    }

    ${desktop`
      box-shadow: inset 0 0 0 1px ${borderLight}, 0 1px 2px 0 rgba(0, 0, 0, 0.08);
      ${
        isOpen &&
        css`
          box-shadow: inset 0 0 0 1px ${borderLight};
        `
      }
    `}
    ${isOpen &&
    css`
      border-bottom: 0;
      border-radius: 4px 4px 0 0;
      box-shadow: inset 0 1px 0 0 ${borderLight};
    `}

    ${altButtonStyles}
  `;
});

const CollapsibleContent = styled(Collapsible.Content)(({ theme }) => {
  const { borderLight } = theme.colors;

  const altContentStyles = theme.showBundleStyles
    ? css`
        box-shadow: none;
        border-left: 0;
        border-right: 0;
      `
    : mobile`
      border-left: 0;
      border-radius: 0;
      border-right: 0;
      border-top: 0;
    `;

  return css`
    &:not([data-transition-state='exited']) {
      border-radius: 0 0 4px 4px;
      box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.08);

      border-left: 1px solid ${borderLight};
      border-right: 1px solid ${borderLight};

      ${altContentStyles}
    }
  `;
});

const CollapsibleContentWrapper = styled.div`
  padding: 16px 16px 0;
`;

const ProductDescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 18px 0;
  padding: 0 16px;
`;

const ProductFeaturesItem = styled.li`
  font-size: 14px;
  margin-left: 18px;
`;

const DownStaticIcon = styled(FaChevronDown)`
  color: ${({ theme }) => theme.colors.sublabelBlack};
  font-size: 14px;
`;

const UpStaticIcon = styled(DownStaticIcon)`
  transform: rotate(180deg);
`;

const DetailsIconWrapper = styled.span`
  align-items: center;
  display: flex;

  ${({ theme }) => theme.pdpStyles.detailsSection.accordionLabel}
`;

const DetailsList = styled.ul``;

const dynamicResourceBundleMessages = (resourceBundleIds) =>
  dynamicDefineMessages(resourceBundleIds);

// This is used to dictate which default value to use. If a description
// is not retrievable from CMS, hide it on the page. For other resource
// bundle keys, display the value from the database if it's not retrievable
// from CMS.
const NULLISH_RESOURCE_BUNDLE_IDS = ['description'];

export const sanitizeTextForRbKey = (key) => {
  return key
    ?.toString()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-');
};

export function ProductDescription({ description, ...rest }) {
  return (
    <ProductDescriptionWrapper {...rest}>
      <DetailTitle>
        <FormattedMessage
          defaultMessage="Product Description"
          id="site_pdp.product_description"
        />
      </DetailTitle>
      <DetailContent dangerouslySetInnerHTML={{ __html: description }} />
    </ProductDescriptionWrapper>
  );
}

ProductDescription.propTypes = {
  description: PropTypes.string,
};

function ToggleButton() {
  const { isOpen } = useCollapsibleState();

  return (
    <CollapsibleButton isOpen={isOpen}>
      <DetailsIconWrapper>
        <FormattedMessage defaultMessage="Details" id="site_pdp.details" />
      </DetailsIconWrapper>
      {isOpen ? <UpStaticIcon /> : <DownStaticIcon />}
    </CollapsibleButton>
  );
}

function AccordionContentBase({ product }) {
  const { isOpen } = useCollapsibleState();
  const intl = useIntl();
  const { details, description, tagIdList } = product;
  const productFeatureList = useConfigValue('public.productFeatureList');

  // temporary shim to continue using details.features if productFeaturesList is empty
  // after transitioning to managing this list in builder then we can remove the details.features fallback
  const isBuilderFeatures = !!productFeatureList?.length;
  const features = isBuilderFeatures
    ? productFeatureList.filter((feature) => tagIdList.includes(feature?.id))
    : details?.features;

  const focusElement = useRef(null);

  useEffect(() => {
    if (isOpen && focusElement.current) {
      focusElement.current.focus();
    }
  }, [isOpen]);

  if (!details && !description) {
    return null;
  }

  // details doesn't always exist
  let fabricContent, fabricType;
  if (details) {
    fabricContent = details.fabricContent;
    fabricType = details.fabricType;
  }

  /**
   * Retrieves a message from the internationalization messages.
   * If the message is not found, it attempts to retrieve the message using the lowercase version of the id.
   * @param {string} id - The id of the message to retrieve.
   * @returns {string|undefined} - The message corresponding to the id, or undefined if not found.
   */
  const getMessage = (id) => {
    const message = intl.messages[id];
    return message || intl.messages[id.toLowerCase()];
  };

  /**
   * Generates resource bundle messages based on the provided type, key, and ids.
   * It sanitizes the key and constructs resource bundle ids, then retrieves the corresponding messages.
   * @param {string} type - The type of resource bundle (e.g., 'fabric', 'feature').
   * @param {string} key - The key used to generate the resource bundle id.
   * @param {Array<string>} ids - An array of ids for which to retrieve messages.
   * @param {Map<string, string>} defaultValues - If passed, defaults the values returned.
   * @returns {Object} - An object mapping each id to its corresponding message or key if not found.
   */
  const getResourceBundleMessages = (type, key, ids, defaultValues = false) => {
    if (!details) {
      return {};
    }
    const sanitizedKey = sanitizeTextForRbKey(key);
    const rbIdRoot = 'product_guide';
    const RB_CHAR_LIMIT = 50;

    const resourceBundleIds = Object.fromEntries(
      ids.map((id) => {
        const truncatedKey = sanitizedKey.slice(
          0,
          RB_CHAR_LIMIT - `${rbIdRoot}_${type}_${id}`.length - 1
        );

        return [
          id,
          { id: `site_pdp.${rbIdRoot}_${type}_${truncatedKey}_${id}` },
        ];
      })
    );

    const resourceBundleMessages =
      dynamicResourceBundleMessages(resourceBundleIds);
    return Object.fromEntries(
      ids.map((id) => [
        id,
        getMessage(resourceBundleMessages[id].id) ||
          (NULLISH_RESOURCE_BUNDLE_IDS.includes(id) || defaultValues?.[id]
            ? null
            : key),
      ])
    );
  };

  const { title: fabricTypeTitle, description: fabricTypeDescription } =
    getResourceBundleMessages('fabric', fabricType, ['title', 'description']);

  return (
    <CollapsibleContent isOpen={isOpen}>
      <CollapsibleContentWrapper tabIndex={0} ref={focusElement}>
        {description ? <ProductDescription description={description} /> : null}
        {features?.length ? (
          <ProductDescriptionWrapper>
            <DetailTitle>
              <FormattedMessage
                defaultMessage="Features"
                id="site_pdp.features"
              />
            </DetailTitle>
            <DetailsList>
              {features.map((feature) => {
                // this call can be modified to use feature id only after the configuration is fully transferred to builder
                const rbKey = isBuilderFeatures
                  ? feature.label
                  : feature?.id || feature;
                const { title: featureTitle } = getResourceBundleMessages(
                  'feature',
                  rbKey,
                  ['title'],
                  feature?.id ? { title: feature?.label } : null
                );
                const displayTitle = featureTitle || feature?.label;
                return (
                  <ProductFeaturesItem key={feature}>
                    {displayTitle}
                  </ProductFeaturesItem>
                );
              })}
            </DetailsList>
          </ProductDescriptionWrapper>
        ) : null}
        <ProductDescriptionWrapper>
          <DetailTitle>
            <FormattedMessage
              defaultMessage="Fabric & Care"
              id="site_pdp.fabric"
            />
          </DetailTitle>
          {fabricType && (
            <>
              <DetailSubTitle>{fabricTypeTitle}</DetailSubTitle>
              {fabricTypeDescription && (
                <DetailContent>{fabricTypeDescription}</DetailContent>
              )}
            </>
          )}
          {fabricContent && (
            <DetailContent data-autotag="pdp-item-fabric-content">
              {fabricContent}
            </DetailContent>
          )}
          <DetailContent>
            <FormattedMessage
              defaultMessage="Imported."
              id="site_pdp.imported"
            />
          </DetailContent>
        </ProductDescriptionWrapper>
      </CollapsibleContentWrapper>
    </CollapsibleContent>
  );
}

AccordionContentBase.propTypes = {
  product: PropTypes.any,
};

const AccordionContent = AccordionContentBase;

const DescriptionBasedAccordionContent = ({ product }) => {
  const { isOpen } = useCollapsibleState();
  const { longDescription, mediumDescription } = product;
  // const longDescription =
  //   'Much more than a T-shirt bra. With a cool-to-the-touch feel and high-shine look, our NEW Microfiber T-Shirt Bra features smooth, lightly padded cups, a sexy caged back, and an infinity-edge neckline that disappears under clothing.';
  // const mediumDescription =
  //   'Underwire\r\nLightly padded\r\nBonded neckline\r\nRecycled microfiber fabric\r\nSupportive rigid front strap\r\nAdjustable and supportive plush-back logo elastic straps\r\nMetal logo plate at left wing\r\nHook-and-eye back closure\r\nBody: 78% Recycled Nylon, 22% Elastane; Wing Lining: 77% Nylon, 23% Elastane\r\nHand wash cold water, separately, lay flat to dry\r\nImported';

  const focusElement = useRef(null);

  useEffect(() => {
    if (isOpen && focusElement.current) {
      focusElement.current.focus();
    }
  }, [isOpen]);

  return (
    <CollapsibleContent isOpen={isOpen}>
      <CollapsibleContentWrapper tabIndex={0} ref={focusElement}>
        {longDescription && (
          <ProductDescription description={longDescription} />
        )}
        {mediumDescription && (
          <ProductDescriptionWrapper>
            <DetailTitle>
              <FormattedMessage
                defaultMessage="Features"
                id="site_pdp.features"
              />
            </DetailTitle>
            <DetailsList>
              {(mediumDescription || '').split(/[\r\n]+/g).map((detail, i) => (
                <ProductFeaturesItem key={i}>{detail}</ProductFeaturesItem>
              ))}
            </DetailsList>
          </ProductDescriptionWrapper>
        )}
      </CollapsibleContentWrapper>
    </CollapsibleContent>
  );
};

DescriptionBasedAccordionContent.propTypes = {
  product: PropTypes.any,
};
export default function ProductDetailsSection({
  product: productFromProps,
  showBundleStyles,
}) {
  const productDetailContext = useProductDetailContext();
  const product = productFromProps || productDetailContext?.product;
  const showFeatureBasedDescription = config.get(
    'public.brand.features.featureBasedDescription'
  );
  if (!product) {
    return null;
  }

  const augmentTheme = (theme) => {
    return {
      ...theme,
      showBundleStyles,
    };
  };

  return (
    <ThemeProvider theme={augmentTheme}>
      <Collapsible defaultOpen={false}>
        <ToggleButton />
        {showFeatureBasedDescription ? (
          <AccordionContent product={product} />
        ) : (
          <DescriptionBasedAccordionContent product={product} />
        )}
      </Collapsible>
    </ThemeProvider>
  );
}

ProductDetailsSection.propTypes = {
  product: PropTypes.any,
  showBundleStyles: PropTypes.bool,
};
